import { resolvePrice } from '../../../shared/utils/productUtils.js';
import { sumSunLightRanges } from '../../../shared/utils/sunLightUtils.js';
import { getInsolationBucket } from '../../../shared/utils/productInsolation.js';
import { buildProductAreaDeciles } from '../../../shared/utils/productAreaDeciles.js';
import { classifyExpositionSides } from '../../../shared/utils/productExpositionSides.js';

import {
	FILTER_IDS,
	COMPONENT_TYPES,
	RANGE_STEPS,
	UNIT_LEVELS,
} from '../constants/filterConstants.js';

/**
 * Context passed to `isSupported` during capability derivation.
 *
 * @typedef {Object} FilterSupportContext
 * @property {boolean} [pricesVisible] - Whether prices display is globally enabled.
 * @property {boolean} [isInsolationSupported] - Whether dataset has valid insolation measurements.
 */

/**
 * Context passed to `apply` during filtering execution.
 *
 * @typedef {Object} FilterApplyContext
 * @property {Object|Array} [compartments]  — Compartments.json definition map or list.
 */
/**
 * FILTER_REGISTRY — Declarative single source of truth for all catalog filters.
 * Order in registry is fallback order in UI
 *
 * @typedef {Object} FilterRegistryEntry
 * @property {string} id                    — Unique identifier matching one of `FILTER_IDS` keys.
 * @property {string} labelKey              — i18n translation key used for UI inputs/cards.
 * @property {string} component             — UI hint matching `COMPONENT_TYPES` (RangeSlider, MultiSelect, etc.).
 * @property {boolean} allowInMainBar       — Whether CMS can promote this filter to the top bar.
 * @property {boolean} supportsStrictMatch  — If true, value shape is `{ selected: string[], strict: boolean }`.
 * @property {number} [step]                — Slider/input step increment for numeric range filters.
 * @property {boolean} [skipActiveCount]    — If true, moving this filter does not increment the active-filter badge count (used for mirrored filters like price_input).
 * @property {string} [placeholderKey]      — i18n key for input/select placeholders.
 * @property {string} [strictTooltipKey]    — i18n key for the strict-match tooltip icon.
 * @property {string} [infoTooltipKey]      — i18n key for informational tooltips next to the filter card label.
 * @property {string} [optionLabelPrefix]   — i18n prefix for multi-select options (e.g. 'compartments').
 * @property {function(*, FilterSupportContext=): boolean} [isSupported] - Pure guard function returning whether the filter is valid for current data/settings.
 * @property {function(Array<Object>, *, FilterApplyContext=): Array<Object>|null} [apply] - Pure filtering function `(products, value, context) => filteredProducts`. Returning `null` marks it as a stub.
 */
export const FILTER_REGISTRY = [
	// ── Main bar filters (sliders) ─────────────────────────────────────────────────────

	{
		id: FILTER_IDS.ROOMS,
		labelKey: 'catalog.rooms',
		component: COMPONENT_TYPES.RANGE_SLIDER,
		supportsStrictMatch: false,
		step: RANGE_STEPS[FILTER_IDS.ROOMS],
		isSupported: (range) =>
			Array.isArray(range) && range.length === 2 && range[0] !== range[1],
		/** @param {LocalProduct[]} products @param {[number, number]} value */
		apply(products, value) {
			return products.filter((p) => {
				const rooms = p.rooms;
				if (rooms == null) return false;
				return rooms >= value[0] && rooms <= value[1];
			});
		},
	},
	{
		id: FILTER_IDS.AREA,
		labelKey: 'catalog.area',
		component: COMPONENT_TYPES.RANGE_SLIDER,
		supportsStrictMatch: false,
		step: RANGE_STEPS[FILTER_IDS.AREA],
		isSupported: (range) =>
			Array.isArray(range) && range.length === 2 && range[0] !== range[1],
		/**
		 * Area range uses floor(min)/floor(max) tick boundaries (step = entry.step).
		 * Each tick N is the lower edge of the bucket [N, N+step):
		 *   lo: exact lower bound (>= value[0]) — real products always >= lo tick so safe
		 *   hi: open upper bound (< value[1] + step) — includes fractional values in the top bucket
		 *
		 * Examples (step=1, range=[42,73]):
		 *   [42,42] → area >= 42 && area < 43  — catches 42.3 ✓
		 *   [73,73] → area >= 73 && area < 74  — catches 73.8 ✓
		 *   [43,50] → area >= 43 && area < 51  — blocks 42.3 ✓
		 *
		 * @param {LocalProduct[]} products
		 * @param {[number, number]} value
		 */
		apply(products, value) {
			const s = RANGE_STEPS[FILTER_IDS.AREA];
			return products.filter((p) => {
				const area = p.area_original;
				if (!area) return false;
				return area >= value[0] && area < value[1] + s;
			});
		},
	},
	{
		id: FILTER_IDS.PRICE_RANGE,
		labelKey: 'catalog.price_range',
		component: COMPONENT_TYPES.RANGE_SLIDER,
		supportsStrictMatch: false,
		step: RANGE_STEPS[FILTER_IDS.PRICE_RANGE],
        isSupported: (range, { pricesVisible } = {}) => {
            if (pricesVisible === false) return false
            return Array.isArray(range)
        },
		/**
		 * Price range uses floor(min/step)/floor(max/step) tick boundaries (step = entry.step).
		 * Same half-open bucket logic as area — see area.apply comment.
		 *
		 * @param {LocalProduct[]} products
		 * @param {[number, number] | null} value
		 */
		apply(products, value) {
			if (value === null) return products;
			const s = RANGE_STEPS[FILTER_IDS.PRICE_RANGE];
			return products.filter((p) => {
				const price = resolvePrice(p);
				if (price === null) return false;
				return price >= value[0] && price < value[1] + s;
			});
		},
	},

	// ── Additional filters ─────────────────────────────────────────────────────

	{
		id: FILTER_IDS.PRICE_INPUT,
		labelKey: 'catalog.price_input',
		component: COMPONENT_TYPES.RANGE_INPUTS,
		supportsStrictMatch: false,
		step: RANGE_STEPS[FILTER_IDS.PRICE_RANGE],
		// UI mirror of price_range — same value, same effect on filtered products.
		// Excluded from the active-filter badge count so moving either control
		// only adds 1 to the count, not 2.
		skipActiveCount: true,
        isSupported: (range, { pricesVisible } = {}) => {
            if (pricesVisible === false) return false
            return Array.isArray(range)
        },
		/**
		 * Identical filtering logic to price_range — same half-open bucket, same step.
		 * Separate registry entry so it can be positioned independently in the panel
		 * (slider in main bar, text inputs in additional panel).
		 *
		 * @param {LocalProduct[]} products
		 * @param {[number, number]|null} value
		 * */
		apply(products, value) {
			if (value === null) return products;
			const s = RANGE_STEPS[FILTER_IDS.PRICE_RANGE];
			return products.filter((p) => {
				const price = resolvePrice(p);
				if (price === null) return false;
				return price >= value[0] && price < value[1] + s;
			});
		},
	},
	{
		id: FILTER_IDS.STATUS,
		labelKey: 'catalog.status',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: false,
		isSupported: (range) => Array.isArray(range) && range.length >= 1,
		/** @param {LocalProduct[]} products @param {string[]} value */
		apply(products, value) {
			return products.filter((p) => value.includes(p.status));
		},
	},
	{
		id: FILTER_IDS.BUILDINGS,
		labelKey: 'catalog.buildings',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: false,
		isSupported: (range) => Array.isArray(range) && range.length > 1,
		/**
		 * Matches against building_name (display name) which is set on every product
		 * by useProducts enrichment. Falls back to building_id for products that
		 * @param {LocalProduct[]} products @param {string[]} value */
		apply(products, value) {
			return products.filter((p) =>
				value.some((v) => v === (p.building_name ?? p.building_id)),
			);
		},
	},
	{
		id: FILTER_IDS.TIERS,
		labelKey: 'catalog.tiers',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: false,
		isSupported: (range) => Array.isArray(range) && range.length > 1,
		/**
		 * Multi-level products: p.tiers is an array of objects { ref_tier, windows, cameras, ... }.
		 * A product passes if any of its tier entries matches a selected ref_tier.
		 *
		 * Values are coerced to numbers because MultiSelect always emits strings
		 * while product data stores ref_tier as numeric.
		 *
		 * @param {LocalProduct[]} products
		 * @param {(number|string)[]} value
		 */
		apply(products, value) {
			const numericTiers = value.map(Number);
			return products.filter((p) => {
				const productTiers = Array.isArray(p.tiers) ? p.tiers : [];
				return productTiers.some((t) => numericTiers.includes(t.ref_tier));
			});
		},
	},
	{
		id: FILTER_IDS.UNIT_LEVELS,
		labelKey: 'catalog.unit_levels',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: false,
		isSupported: (range) => Array.isArray(range) && range.length > 1,
		/**
		 * Classifies each product as 'single' or 'multi' based on the unique set of
		 * ref_tier values across its compartments array.
		 * Products with 0 or 1 unique tiers → 'single'.
		 * Products with 2+ unique tiers     → 'multi'.
		 *
		 * @param {LocalProduct[]} products
		 * @param {('single'|'multi')[]} value — selected level categories
		 */
		apply(products, value) {
			return products.filter((p) => {
				const refs = new Set(
					(p.compartments ?? [])
						.map((c) => c.ref_tier)
						.filter((r) => r != null),
				);
				const level = refs.size > 1 ? UNIT_LEVELS.MULTI : UNIT_LEVELS.SINGLE;
				return value.includes(level);
			});
		},
	},
	// ── Strict-match filter ──────────────────────────────────────────────────
	{
		id: FILTER_IDS.EXTERNAL_AREAS,
		labelKey: 'catalog.external_areas',
		placeholderKey: 'catalog.placeholder_external_areas',
		strictTooltipKey: 'catalog.strict_match_external_tooltip',
		optionLabelPrefix: 'compartments',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: true,
		isSupported: (range) =>
			Array.isArray(range?.options) && range.options.length > 0,
		/**
		 * Filters based on outdoor compartment types derived once from compartments.json passed to method.
		 *
		 * Strict mode table (see documentation):
		 *   empty selected    → only products with NO outdoor areas at all
		 *   [balcony]         → only products whose outdoor areas are EXACTLY {balcony}
		 *   [terrace, balcony] → only products whose outdoor areas are EXACTLY {terrace, balcony}
		 *
		 * Non-strict passthrough: products with no outdoor areas always pass through.
		 * (baseline products without any outdoor space — valid product state)
		 *
		 * @param {LocalProduct[]} products
		 * @param {{ selected: string[], strict: boolean }} value
		 */
		apply(products, value, { outdoorTypes = [] } = {}) {
			const { selected, strict } = value;
			return products.filter((p) => {
				const allCompartmentIds = (p.compartments ?? []).map((c) => c.name_cad);
				// Narrow to OutdoorSpace types for accurate set comparison.
				// Falls back to all compartment IDs if method received no compartments data.
				const productOutdoorAreas =
					outdoorTypes.length > 0
						? allCompartmentIds.filter((id) => outdoorTypes.includes(id))
						: allCompartmentIds;

				if (strict) {
					// Product must have EXACTLY the selected outdoor types — nothing more, nothing less.
					if (selected.length === 0) return productOutdoorAreas.length === 0;
					const areasSet = new Set(productOutdoorAreas);
					return (
						areasSet.size === selected.length &&
						selected.every((s) => areasSet.has(s))
					);
				} else {
					// Non-strict passthrough: products with no outdoor areas always pass through.
					return (
						productOutdoorAreas.length === 0 ||
						selected.some((s) => productOutdoorAreas.includes(s))
					);
				}
			});
		},
	},
	// ── Strict-match filter ──────────────────────────────────────────────────
	{
		id: FILTER_IDS.EXPOSITION,
		labelKey: 'catalog.exposition',
		placeholderKey: 'catalog.placeholder_exposition',
		strictTooltipKey: 'catalog.strict_match_exposition_tooltip',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: true,
		isSupported: (range) =>
			Array.isArray(range?.options) && range.options.length > 0,
		/** @param {LocalProduct[]} products @param {{ selected: string[], strict: boolean }} value */
		apply(products, value) {
			const { selected, strict } = value;
			return products.filter((p) => {
				const expo = p.exposition ?? [];
				if (strict) {
					// Product must have EXACTLY the selected directions — nothing more, nothing less.
					if (selected.length === 0) return expo.length === 0;
					const expoSet = new Set(expo);
					return (
						expoSet.size === selected.length &&
						selected.every((s) => expoSet.has(s))
					);
				} else {
					// Non-strict passthrough: products with no exposition data are always considered valid.
					return expo.length === 0 || selected.some((s) => expo.includes(s));
				}
			});
		},
	},
	{
		id: FILTER_IDS.INSOLATION,
		labelKey: 'catalog.insolation',
		infoTooltipKey: 'catalog.insolation_info',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: false,
        isSupported: (range, { isInsolationSupported } = {}) => {
            if (isInsolationSupported === false) return false
            return Array.isArray(range) && range.length > 0
        },		/**
		 * `value` is an array of bucket strings (same keys as getInsolationBucket):
		 * 'null' | '0' | '1' | … | '10'
		 *
		 * A product passes when its bucket string is in the selected set.
		 * Products with null / empty sun_light_ranges → bucket 'null' → only shown
		 * when the 'null' bucket is selected.
		 *
		 * @param {LocalProduct[]} products
		 * @param {string[]} value — selected bucket strings e.g. ['2','3','4']
		 */
		apply(products, value) {
			const selected = new Set(value);
			return products.filter((p) =>
				selected.has(getInsolationBucket(p.sun_light_ranges)),
			);
		},
	},
	{
		id: FILTER_IDS.EXPOSITION_SIDES,
		labelKey: 'catalog.exposition_sides',
		placeholderKey: 'catalog.placeholder_exposition_sides',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: false,
		isSupported: (range) => Array.isArray(range) && range.length > 1,
		/**
		 * Filters products by exposition-sides classification tags.
		 *
		 * Tags per product: '1'/'2'/'3'/'4+' based on direction count,
		 * plus 'corner' when any two directions are exactly 90° apart.
		 * Products with no exposition data (tags = null) are always excluded.
		 * Apply rule: selected ∩ productTags ≠ ∅.
		 *
		 * @param {LocalProduct[]} products
		 * @param {string[]} value — selected tags, e.g. ['corner', '2']
		 */
		apply(products, value) {
			const selected = new Set(value);
			return products.filter((p) => {
				const tags = classifyExpositionSides(p.exposition ?? []);
				if (!tags) return false;
				return tags.some((tag) => selected.has(tag));
			});
		},
	},
	{
		id: FILTER_IDS.CATEGORY,
		labelKey: 'catalog.category',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: false,
		isSupported: (range) => Array.isArray(range) && range.length > 1,
		/**
		 * Filters products by their product_category field.
		 * Options are derived from the ALL_PRODUCT_CATEGORIES enum, preserving
		 * canonical order, restricted to categories present in the dataset.
		 *
		 * @param {LocalProduct[]} products
		 * @param {string[]} value — selected category strings
		 */
		apply(products, value) {
			const selected = new Set(value);
			return products.filter((p) => selected.has(p.product_category));
		},
	},
	{
		id: FILTER_IDS.COMPARE_AREA,
		labelKey: 'catalog.compare_area',
		infoTooltipKey: 'catalog.compare_area_info',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: false,
		isSupported: (range) => Array.isArray(range) && range.length > 0,
		/**
		 * Filters products by their area-comparison decile bucket.
		 * Decile map is computed fresh on every call from the passed-in products
		 * (same pure function used by the COMPARE colorMode).
		 *
		 * Value is an array of decile strings: 'p0'–'p9'.
		 *   p0 = bottom 10%  (smallest)
		 *   p9 = top    10%  (largest)
		 *
		 * Products with no area_original (uuid missing in decile map) are silently
		 * excluded — they have no meaningful bucket.
		 *
		 * @param {LocalProduct[]} products
		 * @param {string[]} value — selected decile strings e.g. ['p4','p5','p6']
		 */
		apply(products, value, { areaDecileMap } = {}) {
			const selected = new Set(value);
			const deciles = areaDecileMap ?? buildProductAreaDeciles(products);
			return products.filter((p) => selected.has(deciles.get(p.uuid)));
		},
	},
	{
		id: FILTER_IDS.UNIT_NAME_SEARCH,
		labelKey: 'catalog.unit_name_search',
		placeholderKey: 'catalog.placeholder_unit_name_search',
		component: COMPONENT_TYPES.TEXT_SEARCH,
		supportsStrictMatch: false,
		isSupported: () => true,
		/**
		 * Case-insensitive substring match against id_original (human-readable unit identifier) and
		 * name_original (human-readable name from localization file).
		 * Empty query returns the full product list — the universal skip rule fires
		 * first in filterProducts, so apply is only called with a non-empty value.
		 *
		 * @param {LocalProduct[]} products
		 * @param {string} value — trimmed search query; never reaches apply when empty
		 */
		apply(products, value) {
			const query = value.trim().toLowerCase();
			if (!query) return products;
			return products.filter((p) => {
				const original = (p.id_original ?? '').toLowerCase();
				const name = (p.name_original ?? '').toLowerCase();
				return original.includes(query) || name.includes(query);
			});
		},
	},

	// ── Stubs ──────────────────────────────────────
	{
		id: FILTER_IDS.SPECIAL_AREAS,
		labelKey: 'catalog.special_reas',
		placeholderKey: 'catalog.placeholder_special_areas',
		strictTooltipKey: 'catalog.strict_match_special_tooltip',
		optionLabelPrefix: 'compartments',
		component: COMPONENT_TYPES.MULTI_SELECT,
		supportsStrictMatch: true,
		isSupported: () => false,
		apply: null, // TODO: pending non-OutdoorSpace compartment filtering strategy
	},
];
